// 订阅套餐API
// 使用相对路径，通过Next.js rewrite规则代理到后端
const API_BASE_URL = typeof window !== 'undefined' 
  ? ''  // 客户端：使用相对路径，通过Next.js rewrite
  : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002');  // 服务端：直接访问后端

// 请求函数
const request = async (endpoint: string, options: RequestInit = {}) => {
  const basePath = typeof window !== 'undefined' ? '/api' : `${API_BASE_URL}/api`;
  const url = `${basePath}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  };

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    ...options,
    headers
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API请求失败:', error);
    throw error;
  }
};

// 套餐接口类型
export interface SubscriptionPlan {
  id: string;
  plan_key: string;
  name: string;
  description: string;
  price: number;
  album_quota: number;
  features: {
    // 营销文案标签（显示在套餐名称下方）
    tagline?: string;
    // 自定义功能列表（优先使用）
    // 每个功能项可以是字符串（向后兼容）或对象 { text: string, icon?: 'check' | 'cross' | 'dash' }
    feature_list?: (string | { text: string; icon?: 'check' | 'cross' | 'dash'; scope?: 'period' | 'permanent' })[];
    /** 套餐级默认长期权益文案（未配置则用平台默认） */
    permanent_benefits?: string[];
    // 业务逻辑配置项（用于系统限制和计算）
    music_distribution?: boolean;
    personal_broker?: boolean;
    copyright_monitoring?: boolean;
    copyright_registration_free?: number;
    ai_broker_daily_quota?: number;
    ai_broker_group?: boolean;
    broker_service_level?: string;
    data_stats_frequency?: string;
    revenue_withdrawal_time_china?: string;    // 中国市场收益提现时间（如 "3个工作日内"）
    revenue_withdrawal_time_overseas?: string; // 海外市场收益提现时间（如 "14个工作日内"）
    revenue_withdrawal_time?: string; // 向后兼容：旧字段（已废弃，使用 revenue_withdrawal_time_china/overseas）
    release_time?: string; // 发行时间（如 "7个工作日内"）
    promotion_discount?: number;
    description?: string;
  };
  is_active: boolean;
  display_order: number;
}

// 用户订阅接口类型
export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  plan_key: string;
  status: 'active' | 'expired' | 'cancelled';
  start_date: string;
  end_date: string;
  remaining_album_quota: number;
  total_album_quota: number;
  /** 本周期管理员赠送发行额度剩余 */
  bonus_album_quota?: number;
  remaining_copyright_free_quota?: number;
  total_copyright_free_quota?: number;
  agreement_agreed_at: string | null;
  agreement_version: string | null;
  auto_renew_enabled?: number | boolean;
  created_at: string;
  updated_at: string;
  plan_name?: string;
  plan_description?: string;
  plan_price?: number;
  plan_album_quota?: number;
  plan_features?: {
    music_distribution: boolean;
    personal_broker: boolean;
    description: string;
  };
}

// 协议版本接口类型
export interface AgreementVersion {
  agreement_type: string;
  version: string;
  title: string | null;
  content: string | null;
  effective_date: string | null;
}

// 使用记录接口类型
export interface UsageLog {
  id: string;
  subscription_id: string;
  album_id: string | null; // AI经纪人使用记录时可为null
  usage_type: 'album_creation' | 'ai_broker_question';
  quota_used: number;
  created_at: string;
  album_title: string | null;
}

// 订阅API
export const subscriptionApi = {
  // 获取套餐列表
  getPlans: async (): Promise<{ success: boolean; data: { plans: SubscriptionPlan[] } }> => {
    return request('/subscription/plans');
  },

  // 获取当前用户订阅
  getCurrentSubscription: async (): Promise<{ success: boolean; data: { subscription: UserSubscription | null } }> => {
    return request('/subscription/current');
  },

  // 计算订阅费用；可选 promotion_code 应用渠道首年折扣（升级不适用）
  getSubscriptionAmount: async (
    plan_key: string,
    promotion_code?: string | null
  ): Promise<{
    success: boolean;
    data: {
      action: string;
      amount: number;
      new_period_end?: string;
      currentPlan: UserSubscription | null;
      targetPlan: SubscriptionPlan;
      subscription_list_amount?: number;
      subscription_discount_percent?: number;
      promotion?: {
        id: string;
        channel_label: string | null;
        code: string;
        discount_display_note?: string | null;
      };
    };
  }> => {
    const q = new URLSearchParams();
    q.set('plan_key', plan_key);
    const pc = promotion_code != null && String(promotion_code).trim() ? String(promotion_code).trim() : '';
    if (pc) q.set('promotion_code', pc);
    return request(`/subscription/amount?${q.toString()}`);
  },

  // 订阅套餐
  subscribePlan: async (plan_key: string, agreement_version?: string): Promise<{ success: boolean; data: { subscription: UserSubscription } }> => {
    return request('/subscription/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan_key, agreement_version })
    });
  },

  // 获取协议版本
  getAgreementVersion: async (agreement_type: string): Promise<{ success: boolean; data: AgreementVersion }> => {
    return request(`/subscription/agreement-version?agreement_type=${encodeURIComponent(agreement_type)}`);
  },

  // 同意协议（带签署来源留痕）
  agreeAgreement: async (
    agreement_type: string,
    agreement_version: string,
    context?: { source_page?: string; order_id?: string | null }
  ): Promise<{ success: boolean; data: { message: string; agreement_type?: string; agreement_version?: string; subscription_updated?: boolean } }> => {
    return request('/subscription/agree-agreement', {
      method: 'POST',
      body: JSON.stringify({
        agreement_type,
        agreement_version,
        source_page: context?.source_page ?? null,
        order_id: context?.order_id ?? null
      })
    });
  },

  // 获取协议同意状态
  getAgreementStatus: async (): Promise<{ success: boolean; data: { required_agreements: AgreementVersion[]; all_agreed: boolean } }> => {
    return request('/subscription/agreement-status');
  },

  // 获取使用记录
  getUsageLogs: async (limit: number = 50): Promise<{ success: boolean; data: { logs: UsageLog[] } }> => {
    return request(`/subscription/usage?limit=${limit}`);
  },

  // 关闭自动续费
  cancelAutoRenew: async (): Promise<{ success: boolean; data: { subscription: UserSubscription; message: string } }> => {
    return request('/subscription/cancel-auto-renew', { method: 'POST' });
  },

  // 开启自动续费
  enableAutoRenew: async (): Promise<{ success: boolean; data: { subscription: UserSubscription; message: string } }> => {
    return request('/subscription/enable-auto-renew', { method: 'POST' });
  },

  // 兑换码兑换订阅（营销活动）
  redeem: async (code: string): Promise<{ success: boolean; data: { subscription: UserSubscription; message: string }; error?: string }> => {
    return request('/subscription/redeem', {
      method: 'POST',
      body: JSON.stringify({ code: code.trim() })
    });
  }
};
